import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@chakra-ui/react";
import { MdSend } from "react-icons/md";
import { useMutation } from "@apollo/client";
import TooltipIconButton from "components/Shared/TooltipIconButton";
import { serverGql } from "__generated__/serverGql";
import serverClient from "utils/apollo/serverClient";
import useAsyncCompleteToast from "components/Shared/useAsyncCompleteToast";

const mutation = serverGql(/* ServerGraphQL */ `
  mutation ResendBrandUserInvite($brandUserId: UUID!) {
    resendBrandUserInvite(brandUserId: $brandUserId) {
      brandUser {
        id
        user {
          id
          email
        }
      }
    }
  }
`);

type Props = {
  brandUserId: UUID;
};

export default function ResendInviteButton({ brandUserId }: Props) {
  const { showSuccessToast, showErrorToast } = useAsyncCompleteToast();

  const [performMutation, { loading }] = useMutation(mutation, {
    client: serverClient,
    variables: { brandUserId },
  });

  const [isDisabled, setIsDisabled] = useState(false);
  const disabledTimeoutRef = useRef<NodeJS.Timeout>();

  const tempDisable = () => {
    setIsDisabled(true);
    disabledTimeoutRef.current = setTimeout(() => {
      setIsDisabled(false);
    }, 60 * 1000);
  };

  const clearDisabledTimeout = useCallback(() => {
    if (disabledTimeoutRef.current) {
      clearTimeout(disabledTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    setIsDisabled(false);
    clearDisabledTimeout();
    return () => {
      clearDisabledTimeout();
    };
  }, [brandUserId, clearDisabledTimeout]);

  return (
    <TooltipIconButton
      label="Resend Invite"
      icon={<Icon as={MdSend} />}
      size="sm"
      variant="ghost"
      colorScheme="lightPinkButton"
      tooltipProps={{ placement: "bottom" }}
      isLoading={loading}
      isDisabled={isDisabled}
      onClick={() => {
        performMutation()
          .then((result) => {
            const { email } =
              result.data?.resendBrandUserInvite?.brandUser?.user || {};
            showSuccessToast("User invite successfully resent", {
              description:
                email && `Invite was successfully resent to ${email}.`,
            });
            tempDisable();
          })
          .catch(showErrorToast);
      }}
    />
  );
}
